// src/DeviceSettings.js
import React from "react";
import PropTypes from "prop-types";
import { useSectionCollapse } from "./hooks/useSectionCollapse";
import "./DeviceState.css";
import "./DeviceSettings.css";

const labelMapping = {
  // Credential Reader Settings
  appleNFC: "Apple NFC",
  androidHce: "Android NFC",
  mifare2Go: "Mifare2Go",
  mifareDesfireLight: "Mifare Desfire Light",
  mifareDesfire: "Mifare Desfire",
  mifareClassic: "Mifare Classic",
  nfcConfigCard: "NFC Config Cards",
  iClassCredentials: "iClass Credential Mode",
  iso14443SecurityMethod: "ISO 14443 Security",
  iso15693SecurityMethod: "ISO 15693 Security",
  // Lock Event Settings
  keyOverrideEventEnabled: "Key Override Events",
  unauthorizedUserEventEnabled: "Unauthorized User Events",
  rexEventEnabled: "Request to Exit Events",
  interiorLockBlinkingEnabled: "Interior Lock Blinking",
  doubleTapEnabled: "Double Tap Feature",
  bruteForceMitigationSetting: "Brute Force Mitigation",
  powerFailLockMode: "Power Fail Lock Mode",
  blockConstructionModeEnabled: "Construction Mode",
  // BLE Credential Settings
  bleMobileCredentialSetting: "BLE Mobile Credentials",
  bleCredentialPerformance: "BLE Performance",
  bleCredentialReadingRange: "BLE Reading Range",
  // Beeper Settings
  beeperSetting: "Beeper",
  // Door Event Settings
  doorProppedEventSettings: "Door Propped Events",
  doorClosedEventSettings: "Door Closed Events",
  doorOpenedEventSettings: "Door Opened Events",
  proppedDoorAlertThreshold: "Propped Door Alert Threshold",
  // Reader Settings
  antiPassbackDuration: "Anti-Passback Duration",
  // Connectivity Settings
  receiveWifiConnectionEvents: "Wi-Fi Connection Events",
  // Wi-Fi Settings
  wifiConnectivitySetting: "Wi-Fi Enabled Status",
  // Time Zone Settings
  timeZoneOffset: "Timezone Offset",
  dstSetting: "Daylight Savings Time",
  // Autolock Settings
  extendedAutoRelockDelay: "Extended Autolock Delay",
  autoRelockDelay: "Autolock Delay",
  // Lock Mode
  lockMode: "Lock Mode",
  // No Tour (ENGAGE)
  noTour: "No Tour",
  noTourState: "No Tour State",
  noTourDoorId: "No Tour Door ID",
  noTourGroupIds: "No Tour Group IDs",
  noTourEncryptedSiteKey: "No Tour Site Key",
};

const CREDENTIAL_SETTINGS_KEYS = [
  "appleNFC", "androidHce", "mifare2Go", "mifareDesfireLight", "mifareDesfire",
  "mifareClassic", "nfcConfigCard", "iClassCredentials", "iso14443SecurityMethod", "iso15693SecurityMethod",
];

const LOCK_EVENT_SETTINGS_KEYS = [
  "keyOverrideEventEnabled", "unauthorizedUserEventEnabled", "rexEventEnabled",
  "interiorLockBlinkingEnabled", "doubleTapEnabled", "bruteForceMitigationSetting",
  "powerFailLockMode", "blockConstructionModeEnabled",
];

const BLE_CREDENTIAL_SETTINGS_KEYS = [
  "bleMobileCredentialSetting", "bleCredentialPerformance", "bleCredentialReadingRange",
];

const BEEPER_SETTINGS_KEYS = ["beeperSetting"];

const DOOR_EVENT_SETTINGS_KEYS = [
  "doorProppedEventSettings", "doorClosedEventSettings",
  "doorOpenedEventSettings", "proppedDoorAlertThreshold",
];

const READER_SETTINGS_KEYS = ["antiPassbackDuration"];

const CONNECTIVITY_SETTINGS_KEYS = ["receiveWifiConnectionEvents"];

const WIFI_SETTINGS_KEYS = ["wifiConnectivitySetting"];

const TIMEZONE_SETTINGS_KEYS = ["timeZoneOffset", "dstSetting"];

const AUTOLOCK_SETTINGS_KEYS = ["extendedAutoRelockDelay", "autoRelockDelay"];

const LOCK_MODE_KEYS = ["lockMode"];

const NOTOUR_KEYS = ["noTour", "noTourState", "noTourDoorId", "noTourGroupIds", "noTourEncryptedSiteKey"];

const INTEGER_SETTING_KEYS = [
  "proppedDoorAlertThreshold", "extendedAutoRelockDelay", "autoRelockDelay", "antiPassbackDuration",
  "timeZoneOffset",
];

const ALL_SETTINGS_KEYS = [
  ...CREDENTIAL_SETTINGS_KEYS,
  ...LOCK_EVENT_SETTINGS_KEYS,
  ...BLE_CREDENTIAL_SETTINGS_KEYS,
  ...BEEPER_SETTINGS_KEYS,
  ...DOOR_EVENT_SETTINGS_KEYS,
  ...READER_SETTINGS_KEYS,
  ...CONNECTIVITY_SETTINGS_KEYS,
  ...WIFI_SETTINGS_KEYS,
  ...TIMEZONE_SETTINGS_KEYS,
  ...AUTOLOCK_SETTINGS_KEYS,
  ...LOCK_MODE_KEYS,
  ...NOTOUR_KEYS,
];

const SECTIONS = [
  { keys: CREDENTIAL_SETTINGS_KEYS, label: "Credential Reader Settings" },
  { keys: LOCK_EVENT_SETTINGS_KEYS, label: "Lock Event Settings" },
  { keys: BLE_CREDENTIAL_SETTINGS_KEYS, label: "BLE Credential Settings" },
  { keys: BEEPER_SETTINGS_KEYS, label: "Beeper Settings" },
  { keys: DOOR_EVENT_SETTINGS_KEYS, label: "Door Event Settings" },
  { keys: READER_SETTINGS_KEYS, label: "Reader Settings" },
  { keys: CONNECTIVITY_SETTINGS_KEYS, label: "Connectivity Settings" },
  { keys: WIFI_SETTINGS_KEYS, label: "Wi-Fi Settings" },
  { keys: TIMEZONE_SETTINGS_KEYS, label: "Time Zone Settings" },
  { keys: AUTOLOCK_SETTINGS_KEYS, label: "Autolock Settings" },
  { keys: LOCK_MODE_KEYS, label: "Lock Mode" },
  { keys: NOTOUR_KEYS, label: "No Tour" },
];

const DeviceSettings = ({
  deviceState,
  isCollapsed,
  onToggleCollapse,
  dragHandleProps,
  onEditCredentialsSettingClick,
}) => {
  const { isSectionCollapsed, toggleSection } = useSectionCollapse();

  if (!deviceState) return null;

  const presentKeys = ALL_SETTINGS_KEYS.filter(
    (key) => deviceState[key] !== undefined && deviceState[key] !== null
  );

  if (presentKeys.length === 0) return null;

  return (
    <div className="device-state-container device-settings-container">
      <h2 onClick={onToggleCollapse} {...dragHandleProps}>
        Device Settings
        <span className="collapse-icon">{isCollapsed ? "▼" : "▲"}</span>
      </h2>

      {!isCollapsed && (
        <div className="state-grid">
          {/* Expanded sections — render full header + content in defined order */}
          {SECTIONS.map(({ keys, label }) => {
            const presentSectionKeys = keys.filter(
              (key) => deviceState[key] !== undefined && deviceState[key] !== null
            );
            if (presentSectionKeys.length === 0 || isSectionCollapsed(label)) return null;

            return (
              <React.Fragment key={label}>
                <div
                  className="device-state-section-header device-state-section-header--clickable"
                  onClick={() => toggleSection(label)}
                >
                  <span>{label}</span>
                  <span className="section-collapse-icon">▼</span>
                </div>
                {presentSectionKeys.map((key) => {
                  const value = deviceState[key];
                  const isInteger = INTEGER_SETTING_KEYS.includes(key);

                  return (
                    <div key={key} className="state-item">
                      <span className="state-label">{labelMapping[key] || key}:</span>
                      <span className="state-value">
                        {isInteger ? (
                          String(value)
                        ) : (
                          <span className={value !== "DISABLED" ? "setting-enabled" : "setting-disabled"}>
                            {value}
                          </span>
                        )}
                        {onEditCredentialsSettingClick && (
                          <button
                            className="edit-display-name-button"
                            onClick={() => onEditCredentialsSettingClick(key, value)}
                            title={`Edit ${labelMapping[key] || key}`}
                            aria-label={`Edit ${labelMapping[key] || key}`}
                          >
                            ✏️
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}

          {/* Collapsed sections — rendered as chips in a single compact row */}
          {(() => {
            const chips = SECTIONS.filter(({ keys, label }) => {
              const hasKeys = keys.some(
                (key) => deviceState[key] !== undefined && deviceState[key] !== null
              );
              return hasKeys && isSectionCollapsed(label);
            });
            if (chips.length === 0) return null;
            return (
              <div className="device-state-chips-bar">
                {chips.map(({ label }) => (
                  <button
                    key={label}
                    className="device-state-section-chip"
                    onClick={() => toggleSection(label)}
                    title={`Expand ${label}`}
                  >
                    {label} <span className="chip-expand-icon">▶</span>
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

DeviceSettings.propTypes = {
  deviceState: PropTypes.object,
  isCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func.isRequired,
  dragHandleProps: PropTypes.object,
  onEditCredentialsSettingClick: PropTypes.func,
};

export default DeviceSettings;
